import {
  collection,
  doc,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Capture, CaptureStatus, CaptureType } from '@/types';

// ============================================
// Collection Path Helpers
// ============================================

// Note: Captures use a root-level collection with userId field (not nested under users)
const getCapturesRef = () => collection(db, 'captures');
const getCaptureRef = (captureId: string) => doc(db, 'captures', captureId);

// ============================================
// Document Converters
// ============================================

export const docToCapture = (docSnap: QueryDocumentSnapshot<DocumentData>): Capture => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId,
    content: data.content,
    type: data.type || 'thought',
    createdAt: data.createdAt?.toDate?.() || new Date(),
    processedAt: data.processedAt?.toDate?.() || undefined,
    status: data.status || 'inbox',
    convertedTo: data.convertedTo || undefined,
    tags: data.tags || [],
  };
};

// ============================================
// Input Types
// ============================================

export interface CreateCaptureInput {
  content: string;
  type?: CaptureType;
  tags?: string[];
}

export interface UpdateCaptureInput {
  content?: string;
  type?: CaptureType;
  tags?: string[];
  status?: CaptureStatus;
}

// ============================================
// CRUD Operations
// ============================================

export async function createCapture(userId: string, data: CreateCaptureInput): Promise<Capture> {
  const now = Timestamp.now();
  const newCaptureData = {
    userId,
    content: data.content.trim(),
    type: data.type || 'thought',
    status: 'inbox' as CaptureStatus,
    tags: data.tags || [],
    createdAt: now,
    updatedAt: now,
    processedAt: null,
    convertedTo: null,
  };

  const capturesRef = getCapturesRef();
  const docRef = await addDoc(capturesRef, newCaptureData);

  return {
    id: docRef.id,
    ...newCaptureData,
    createdAt: now.toDate(),
    processedAt: undefined,
    convertedTo: undefined,
  };
}

export async function updateCapture(captureId: string, updates: UpdateCaptureInput): Promise<void> {
  const captureRef = getCaptureRef(captureId);
  await updateDoc(captureRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCapture(captureId: string): Promise<void> {
  const captureRef = getCaptureRef(captureId);
  await deleteDoc(captureRef);
}

// ============================================
// Processing Operations
// ============================================

export async function processCapture(
  captureId: string,
  convertTo?: 'task' | 'block',
  convertedId?: string
): Promise<void> {
  const captureRef = getCaptureRef(captureId);
  const now = Timestamp.now();

  if (convertTo && convertedId) {
    await updateDoc(captureRef, {
      status: 'processed',
      processedAt: now,
      updatedAt: serverTimestamp(),
      convertedTo: {
        type: convertTo,
        id: convertedId,
      },
    });
  } else {
    await updateDoc(captureRef, {
      status: 'processed',
      processedAt: now,
      updatedAt: serverTimestamp(),
    });
  }
}

export async function archiveCapture(captureId: string): Promise<void> {
  const captureRef = getCaptureRef(captureId);
  await updateDoc(captureRef, {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToCaptures(
  userId: string,
  onData: (captures: Capture[]) => void,
  onError?: (error: Error) => void
): () => void {
  const capturesRef = getCapturesRef();
  const capturesQuery = query(
    capturesRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    capturesQuery,
    (snapshot) => {
      const capturesData: Capture[] = snapshot.docs.map(docToCapture);
      onData(capturesData);
    },
    (error) => {
      console.error('Error listening to captures:', error);
      onError?.(error);
    }
  );
}
